'use client';

import React from 'react';

export type CheckboxProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> & {
  /** Diameter of the circular box in px. Default 18. */
  boxSize?: number;
};

/**
 * Canonical checkbox for the client app, a circular, brand-navy checkbox.
 * The native input is kept (visually restyled via `appearance-none`) so it
 * stays fully accessible and form-compatible; an overlaid checkmark is
 * revealed with `peer-checked`. Use everywhere a checkbox is needed.
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { className = '', boxSize = 18, disabled, ...props },
  ref
) {
  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center ${
        disabled ? 'opacity-50' : ''
      } ${className}`}
      style={{ width: boxSize, height: boxSize }}
    >
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        className="peer absolute inset-0 m-0 cursor-pointer appearance-none rounded-full border border-neutral-15 bg-constant-white transition-colors checked:border-blue-100 checked:bg-blue-100 hover:border-blue-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-15 disabled:cursor-not-allowed"
        {...props}
      />
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
        className="pointer-events-none relative hidden h-[62%] w-[62%] text-constant-white peer-checked:block"
      >
        <path
          d="M5 13l4 4L19 7"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
});

export default Checkbox;
